import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActionIcon,
  Badge,
  Button,
  Group,
  Loader,
  Paper,
  Stack,
  Table,
  Text,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconCategory, IconEdit, IconPlus, IconTrash } from '@tabler/icons-react';
import { PageHeader } from '../../components/PageHeader';
import { EmptyState } from '../../components/EmptyState';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { useCategories, useDeleteCategory } from './categoriesApi';
import { CategoryFormDrawer } from './CategoryFormDrawer';
import type { CategoryDto } from './types';

export function CategoriesPage() {
  const { t, i18n } = useTranslation();
  const { data: categories, isLoading } = useCategories();
  const deleteMutation = useDeleteCategory();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<CategoryDto | null>(null);
  const [deleting, setDeleting] = useState<CategoryDto | null>(null);

  // Parent lookup for the "parent name" column.
  const nameById = useMemo(() => {
    const map = new Map<string, string>();
    (categories ?? []).forEach((c) =>
      map.set(c.categoryId, i18n.language === 'ar' ? c.nameAr : (c.nameEn ?? c.nameAr)),
    );
    return map;
  }, [categories, i18n.language]);

  const openCreate = () => {
    setEditing(null);
    setDrawerOpen(true);
  };

  const openEdit = (category: CategoryDto) => {
    setEditing(category);
    setDrawerOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    try {
      await deleteMutation.mutateAsync(deleting.categoryId);
      notifications.show({
        color: 'green',
        title: t('common.toasts.deleted'),
        message: deleting.nameAr,
      });
      setDeleting(null);
    } catch {
      notifications.show({
        color: 'red',
        title: t('common.toasts.errorTitle'),
        message: t('common.toasts.errorBody'),
      });
    }
  };

  const hasCategories = (categories?.length ?? 0) > 0;

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow={t('nav.sections.catalog')}
        title={t('categories.page.title')}
        subtitle={t('categories.page.subtitle')}
        actions={
          <Button leftSection={<IconPlus size={16} stroke={1.8} />} onClick={openCreate}>
            {t('categories.page.create')}
          </Button>
        }
      />

      <Paper p={hasCategories ? 0 : 'lg'}>
        {isLoading ? (
          <Stack align="center" py={48}>
            <Loader size="sm" />
          </Stack>
        ) : !hasCategories ? (
          <EmptyState
            icon={<IconCategory size={22} stroke={1.6} />}
            title={t('categories.empty.title')}
            description={t('categories.empty.body')}
            actionLabel={t('categories.page.create')}
            onAction={openCreate}
          />
        ) : (
          <Table verticalSpacing="md" highlightOnHover striped="even">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('categories.columns.name')}</Table.Th>
                <Table.Th>{t('categories.columns.parent')}</Table.Th>
                <Table.Th style={{ width: 100 }}>{t('categories.columns.sort')}</Table.Th>
                <Table.Th>{t('categories.columns.status')}</Table.Th>
                <Table.Th style={{ width: 100, textAlign: 'end' }}>
                  {t('common.actions')}
                </Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {categories!.map((c) => (
                <Table.Tr key={c.categoryId}>
                  <Table.Td>
                    <Group gap="sm" wrap="nowrap">
                      <div
                        className="h-3 w-3 shrink-0 rounded-full border border-[var(--wheels-border)]"
                        style={{ backgroundColor: c.colorHex ?? 'transparent' }}
                        aria-hidden="true"
                      />
                      <div>
                        <Text fw={500} c="var(--wheels-ink-strong)">
                          {c.nameAr}
                        </Text>
                        {c.nameEn && (
                          <Text fz="xs" c="var(--wheels-ink-muted)">
                            {c.nameEn}
                          </Text>
                        )}
                      </div>
                    </Group>
                  </Table.Td>
                  <Table.Td c="var(--wheels-ink-muted)">
                    {c.parentCategoryId ? nameById.get(c.parentCategoryId) ?? '—' : '—'}
                  </Table.Td>
                  <Table.Td>
                    <span className="font-numeric text-[var(--wheels-ink-muted)]">
                      {c.sortOrder}
                    </span>
                  </Table.Td>
                  <Table.Td>
                    {c.isActive ? (
                      <Badge color="green" variant="light" radius="sm">
                        {t('common.active')}
                      </Badge>
                    ) : (
                      <Badge color="gray" variant="light" radius="sm">
                        {t('common.inactive')}
                      </Badge>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs" justify="flex-end" wrap="nowrap">
                      <Tooltip label={t('common.edit')} withinPortal>
                        <ActionIcon
                          variant="subtle"
                          color="gray"
                          onClick={() => openEdit(c)}
                          aria-label={t('common.edit')}
                        >
                          <IconEdit size={16} stroke={1.6} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label={t('common.delete')} withinPortal>
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          onClick={() => setDeleting(c)}
                          aria-label={t('common.delete')}
                        >
                          <IconTrash size={16} stroke={1.6} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Paper>

      <CategoryFormDrawer
        opened={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        category={editing}
      />

      <ConfirmDialog
        opened={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={confirmDelete}
        title={t('categories.delete.title')}
        message={t('categories.delete.body', { name: deleting?.nameAr ?? '' })}
        loading={deleteMutation.isPending}
      />
    </Stack>
  );
}
